import type { ContentItem, ContentItemDraft, ContentItemType, Locale, PageTemplateKey, SessionUser, SiteDocument, TenantSummary, ThemeKey, ThemeSectionDefinition } from "../shared/model";

interface ApiErrorBody {
  error?: string;
  issues?: Array<{ path?: string; message?: string }>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: init?.body instanceof Blob
      ? init.headers
      : { "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    let body: ApiErrorBody = {};
    try { body = await response.json() as ApiErrorBody; } catch { body = {}; }
    const validationMessage = body.issues?.find((issue) => issue.message)?.message;
    throw new Error(validationMessage ?? body.error ?? `İstek başarısız (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export const api = {
  bootstrapStatus: () => request<{ required: boolean }>("/api/auth/bootstrap-status"),
  bootstrap: (input: { displayName: string; email: string; password: string; token: string }) =>
    request<{ ok: true }>("/api/auth/bootstrap", { method: "POST", body: JSON.stringify(input) }),
  login: (email: string, password: string) =>
    request<{ user: SessionUser }>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST", body: "{}" }),
  me: () => request<{ user: SessionUser }>("/api/auth/me"),
  changePassword: (currentPassword: string, password: string) =>
    request<{ ok: true }>("/api/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, password }) }),
  tenants: () => request<{ tenants: TenantSummary[] }>("/api/tenants"),
  createTenant: (input: { name: string; slug: string; themeKey: ThemeKey; defaultLocale: Locale; mediaQuotaBytes: number }) =>
    request<{ tenant: TenantSummary }>("/api/owner/tenants", { method: "POST", body: JSON.stringify(input) }),
  updateTenant: (tenantId: string, input: Partial<Pick<TenantSummary, "name" | "status" | "mediaQuotaBytes" | "defaultLocale">>) =>
    request<{ tenant: TenantSummary }>(`/api/owner/tenants/${tenantId}`, { method: "PATCH", body: JSON.stringify(input) }),
  createEditor: (input: { tenantId: string; displayName: string; email: string; password: string }) =>
    request<{ ok: true; userId: string }>("/api/owner/editors", { method: "POST", body: JSON.stringify(input) }),
  documents: (tenantId: string, locale: Locale) =>
    request<{ documents: SiteDocument[] }>(`/api/tenants/${tenantId}/documents?locale=${locale}`),
  createPage: (tenantId: string, input: { locale: Locale; title: string; slug: string; templateKey: PageTemplateKey }) =>
    request<{ document: SiteDocument }>(`/api/tenants/${tenantId}/pages`, { method: "POST", body: JSON.stringify(input) }),
  updateDocument: (tenantId: string, documentId: string, draft: unknown) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/documents/${documentId}`, { method: "PUT", body: JSON.stringify({ draft }) }),
  deletePage: (tenantId: string, documentId: string) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/pages/${documentId}`, { method: "DELETE", body: "{}" }),
  sectionDefinitions: (tenantId: string, templateKey: PageTemplateKey) =>
    request<{ definitions: Omit<ThemeSectionDefinition, "renderVariant">[] }>(`/api/tenants/${tenantId}/section-definitions?templateKey=${templateKey}`),
  contentItems: (tenantId: string, locale: Locale, itemType: ContentItemType) =>
    request<{ items: ContentItem[] }>(`/api/tenants/${tenantId}/content-items?locale=${locale}&type=${itemType}`),
  createContentItem: (tenantId: string, locale: Locale, itemType: ContentItemType, draft: ContentItemDraft) =>
    request<{ item: ContentItem }>(`/api/tenants/${tenantId}/content-items`, { method: "POST", body: JSON.stringify({ locale, itemType, ...draft }) }),
  updateContentItem: (tenantId: string, itemId: string, draft: ContentItemDraft) =>
    request<{ item: ContentItem }>(`/api/tenants/${tenantId}/content-items/${itemId}`, { method: "PUT", body: JSON.stringify({ draft }) }),
  duplicateContentItem: (tenantId: string, itemId: string) =>
    request<{ item: ContentItem }>(`/api/tenants/${tenantId}/content-items/${itemId}/duplicate`, { method: "POST", body: "{}" }),
  deleteContentItem: (tenantId: string, itemId: string) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/content-items/${itemId}`, { method: "DELETE", body: "{}" }),
  reorderContentItems: (tenantId: string, ids: string[]) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/content-items/reorder`, { method: "PATCH", body: JSON.stringify({ ids }) }),
  reinstallTheme: (tenantId: string) =>
    request<{ ok: true; tenant: TenantSummary }>(`/api/owner/tenants/${tenantId}/reinstall-theme`, { method: "POST", body: JSON.stringify({ confirmation: "RESET PORTO" }) }),
  switchTheme: (tenantId: string, themeKey: ThemeKey, reseed = true) =>
    request<{ tenant: TenantSummary }>(`/api/tenants/${tenantId}/theme`, { method: "PUT", body: JSON.stringify({ themeKey, reseed }) }),
  themes: () => request<{ themes: Array<{ id: ThemeKey; name: string; description: string }> }>("/api/themes"),
  publish: (tenantId: string) =>
    request<{ ok: true; tenant: TenantSummary }>(`/api/tenants/${tenantId}/publish`, { method: "POST", body: "{}" }),
  media: (tenantId: string) => request<{ media: MediaAsset[] }>(`/api/tenants/${tenantId}/media`),
  uploadMedia: async (tenantId: string, file: File): Promise<{ id: string; url: string }> => {
    return request<{ id: string; url: string }>(`/api/tenants/${tenantId}/media?filename=${encodeURIComponent(file.name)}`, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
  },
  submissions: (tenantId: string) => request<{ submissions: Submission[] }>(`/api/tenants/${tenantId}/submissions`),
  updateSubmission: (tenantId: string, submissionId: string, status: Submission["status"]) =>
    request<{ ok: true }>(`/api/tenants/${tenantId}/submissions/${submissionId}`, { method: "PATCH", body: JSON.stringify({ status }) }),
};

export interface MediaAsset {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
  url: string;
}

export interface Submission {
  id: string;
  form_type: "contact" | "newsletter";
  locale: Locale;
  status: "new" | "read" | "archived";
  created_at: string;
  payload: { name?: string; email?: string; phone?: string; message?: string };
}
